import { useMemo } from "react";
import { ProjectNode, TableauProject } from "../types";

const SUBPROJECT_NAME = "altimate_workbooks";

export const buildProjectTree = (projects: TableauProject[]): ProjectNode[] => {
  const projectMap = new Map<string, ProjectNode>();
  const roots: ProjectNode[] = [];
  projects.forEach(project => {
    projectMap.set(project.id, { ...project, children: [] });
  });
  projects.forEach(project => {
    const node = projectMap.get(project.id)!;
    if (project.parent_project_id) {
      const parent = projectMap.get(project.parent_project_id);
      if (parent) parent.children.push(node);
      else roots.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

interface UseWorkbookPickerStateArgs {
  projects: TableauProject[];
  selectedProject: TableauProject | null;
  canPublishWorkbooks: boolean;
}

interface WorkbookPickerState {
  /** Tableau's built-in "Default" project, if returned by the API. */
  tableauDefaultProject: TableauProject | undefined;
  /** True if the user must pick a custom parent (default location unusable). */
  mustUseCustomLocation: boolean;
  /** Project IDs disabled in the tree picker (logic differs by role). */
  disabledProjectIds: Set<string>;
  /** Whether the currently selected project has the subproject. */
  selectedProjectHasAltimateWorkbooks: boolean;
}

/**
 * Encapsulates the project-tree gating logic for the Tableau workbook picker.
 *
 * The semantics flip based on whether the connecting PAT can publish:
 * - Creator: existence of the `altimate_workbooks` subproject in a parent
 *   BLOCKS picking that parent (clash on publish).
 * - Explorer: existence of the subproject is REQUIRED (we cannot publish, so
 *   we must locate workbooks the customer already uploaded).
 */
export const useWorkbookPickerState = ({
  projects,
  selectedProject,
  canPublishWorkbooks,
}: UseWorkbookPickerStateArgs): WorkbookPickerState => {
  const tableauDefaultProject = useMemo(
    () => projects.find(p => p.name.toLowerCase() === "default" && !p.parent_project_id),
    [projects]
  );

  const altimateWorkbooksProject = useMemo(
    () => projects.find(p => p.name === SUBPROJECT_NAME && !p.parent_project_id),
    [projects]
  );

  const altimateWorkbooksHasChildren = useMemo(() => {
    if (!altimateWorkbooksProject) return false;
    return projects.some(p => p.parent_project_id === altimateWorkbooksProject.id);
  }, [projects, altimateWorkbooksProject]);

  const defaultProjectHasAltimateWorkbooks = useMemo(() => {
    if (!tableauDefaultProject) return false;
    return projects.some(
      p => p.parent_project_id === tableauDefaultProject.id && p.name === SUBPROJECT_NAME
    );
  }, [tableauDefaultProject, projects]);

  const mustUseCustomLocation = canPublishWorkbooks
    ? altimateWorkbooksProject !== undefined && altimateWorkbooksHasChildren
    : // Explorer: only force custom location when the workbooks clearly aren't
      // under Default. We don't know the customer's upload layout so we don't
      // over-gate — the backend pre-flight is the source of truth.
      !defaultProjectHasAltimateWorkbooks;

  const projectsContainingAltimateWorkbooks = useMemo(() => {
    const ids = new Set<string>();
    projects.forEach(project => {
      if (project.name === SUBPROJECT_NAME && project.parent_project_id) {
        ids.add(project.parent_project_id);
      }
    });
    return ids;
  }, [projects]);

  const disabledProjectIds = useMemo(() => {
    if (canPublishWorkbooks) {
      // Creator: disable projects that already contain the subproject
      // (we would clash on publish).
      return projectsContainingAltimateWorkbooks;
    }
    // Explorer: don't disable anything. Customer's upload layout is opaque
    // to us (could be a subproject named anything, or workbooks directly in
    // the picked project). The backend pre-flight validates reachability —
    // if not found, Finish returns an error.
    return new Set<string>();
  }, [canPublishWorkbooks, projectsContainingAltimateWorkbooks]);

  const selectedProjectHasAltimateWorkbooks = useMemo(() => {
    if (!selectedProject) return false;
    return projects.some(
      p => p.parent_project_id === selectedProject.id && p.name === SUBPROJECT_NAME
    );
  }, [selectedProject, projects]);

  return {
    tableauDefaultProject,
    mustUseCustomLocation,
    disabledProjectIds,
    selectedProjectHasAltimateWorkbooks,
  };
};
